import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, TrendingUp, ArrowUp } from 'lucide-react';

export default function SuccessStory() {
  const improvements = [
    {
      before: "47.1%",
      after: "69.2%",
      improvement: "+47%",
      quote: "Before Trading Compass, my win rate was stuck at 47%. After 30 days of following the system, I hit 69.2%.",
      testId: "success-story-1"
    },
    {
      before: "52.4%",
      after: "83.3%",
      improvement: "+59%",
      quote: "I went from 52% to 83% in one month. The difference? Discipline before execution.",
      testId: "success-story-2"
    }
  ];

  return (
    <section className="py-10 md:py-16 bg-slate-50" data-testid="success-story-section">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="text-center mb-12"
        >
          <h2 
            className="text-3xl md:text-4xl font-semibold text-[#337AF7] tracking-tight"
            data-testid="success-story-title"
          >
            Real Traders. Real Results.
          </h2>
          <p className="mt-3 text-[#162959]/70">
            What happens when you follow the system
          </p>
        </motion.div>

        {/* Top Performer Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="max-w-lg mx-auto mb-10"
          data-testid="top-performer-card"
        >
          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-6 md:p-8 text-center">
            {/* Trophy Icon */}
            <div className="w-16 h-16 rounded-full bg-amber-100 border-2 border-amber-300 flex items-center justify-center mx-auto mb-4">
              <Trophy className="h-8 w-8 text-amber-500" />
            </div>
            
            {/* Label */}
            <p className="text-sm font-semibold text-amber-600 mb-3">
              Top Performer This Month
            </p>
            
            {/* Stats */}
            <div className="flex items-center justify-center gap-8 mb-4">
              <div>
                <p className="text-3xl md:text-4xl font-bold text-[#162959]">74.1%</p>
                <p className="text-sm text-[#162959]/60">Win Rate</p>
              </div>
              <div className="h-12 w-px bg-slate-200"></div>
              <div>
                <p className="text-3xl md:text-4xl font-bold text-[#162959]">1:2.3</p>
                <p className="text-sm text-[#162959]/60">Risk-Reward</p>
              </div>
            </div>
            
            <p className="text-xs text-[#162959]/50">
              Tracked inside Trading Compass
            </p>
          </div>
        </motion.div>

        {/* Improvement Stories */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {improvements.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              data-testid={item.testId}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 p-6"
            >
              {/* Quote */}
              <p className="text-[#162959]/80 italic mb-4 leading-relaxed">
                "{item.quote}"
              </p>
              
              {/* Improvement Badge */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-[#162959]/60">30-day improvement</span>
                </div>
                <div className="flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full">
                  <ArrowUp className="h-4 w-4" />
                  <span className="font-semibold">{item.improvement}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-10 text-center text-sm text-[#162959]/50"
        >
          Performance tracked and verified inside Trading Compass
        </motion.p>
      </div>
    </section>
  );
}
